import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

/**
 * Tadlo Button System
 * Variants: primary | secondary | ghost | danger | link
 * Sizes: sm / md / lg / xl / icon / icon-sm / icon-lg
 * Aliases (backwards-compat): default=primary, outline=secondary, destructive=danger
 * Extra props: loading (spinner + disabled), icon + iconPosition, fullWidth, asChild
 */

const PRIMARY = "text-primary-foreground font-semibold bg-gradient-to-b from-[hsl(var(--primary))] to-[hsl(var(--primary)/0.88)] shadow-[0_2px_8px_-2px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(0_0%_100%/0.15)] hover:brightness-[1.06] hover:shadow-[0_6px_18px_-4px_hsl(var(--primary)/0.6),inset_0_1px_0_0_hsl(0_0%_100%/0.18)] dark:shadow-[0_2px_8px_-2px_hsl(var(--primary)/0.55),inset_0_1px_0_0_hsl(0_0%_100%/0.12)]";
const SECONDARY = "bg-background text-foreground border border-border shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-accent hover:border-primary/40 hover:text-foreground dark:bg-card dark:hover:bg-accent/80";
const GHOST = "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground";
const DANGER = "text-destructive-foreground font-semibold bg-gradient-to-b from-[hsl(var(--destructive))] to-[hsl(var(--destructive)/0.88)] shadow-[0_2px_8px_-2px_hsl(var(--destructive)/0.4),inset_0_1px_0_0_hsl(0_0%_100%/0.12)] hover:brightness-[1.05] hover:shadow-[0_6px_18px_-4px_hsl(var(--destructive)/0.55)]";
const LINK = "text-primary underline-offset-4 hover:underline h-auto px-0 py-0 rounded-none shadow-none";
const BASE = "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none font-medium transition-[background,box-shadow,filter,transform,color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50 disabled:pointer-events-none disabled:shadow-none disabled:brightness-100 motion-safe:active:scale-[0.97] [&_svg]:pointer-events-none [&_svg]:shrink-0";

const buttonVariants = cva(BASE, {
  variants: {
    variant: {
      primary: PRIMARY,
      secondary: SECONDARY,
      ghost: GHOST,
      danger: DANGER,
      link: LINK,
      default: PRIMARY,
      outline: SECONDARY,
      destructive: DANGER,
    },
    size: {
      sm: "h-8 px-3 text-xs rounded-[10px] [&_svg]:size-[14px]",
      md: "h-10 px-4 text-sm rounded-[12px] [&_svg]:size-4",
      lg: "h-12 px-6 text-base rounded-[14px] [&_svg]:size-[18px]",
      xl: "h-14 px-7 text-base rounded-[16px] [&_svg]:size-5",
      icon: "h-10 w-10 rounded-[12px] [&_svg]:size-4",
      "icon-sm": "h-8 w-8 rounded-[10px] [&_svg]:size-[14px]",
      "icon-lg": "h-12 w-12 rounded-[14px] [&_svg]:size-[18px]",
      default: "h-10 px-4 text-sm rounded-[12px] [&_svg]:size-4",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
  },
});

const Button = React.forwardRef((props, ref) => {
  const {
    className,
    variant,
    size,
    asChild,
    loading,
    disabled,
    icon,
    iconPosition,
    fullWidth,
    children,
    ...rest
  } = props;

  const Comp = asChild ? Slot : "button";
  const classes = cn(buttonVariants({ variant, size }), fullWidth && "w-full", className);

  if (asChild) {
    return (
      <Comp className={classes} ref={ref} {...rest}>
        {children}
      </Comp>
    );
  }

  const pos = iconPosition || "left";
  const spinner = React.createElement("span", {
    "aria-hidden": "true",
    className: "inline-block w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin",
  });

  let leading = null;
  let trailing = null;
  if (loading) {
    leading = spinner;
  } else if (icon && pos === "left") {
    leading = icon;
  } else if (icon && pos === "right") {
    trailing = icon;
  }

  return (
    <Comp
      className={classes}
      ref={ref}
      disabled={disabled || loading || undefined}
      {...rest}
    >
      {leading}
      {children}
      {trailing}
    </Comp>
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
